import { toast } from 'react-toastify';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';
import Breadcrumb from '../../atoms/Breadcrumb';
import { DraweeDetail } from './types';
import { getBatchDetail, refreshPaf, reprocessBatch, continueAnalysis, BatchDetail, BatchDetailItem } from '../../../services/CreditAnalysisService';
import DraweeDetailsModal from './DraweeDetailsModal';
import { useDebounce } from './useDebounce';
import { unmaskDocument, isCpf } from './utils/documentUtils';
import BatchActionBar from './components/batch/BatchActionBar';

const STATUS_OPTIONS = [
    { value: 'approved', label: 'Aprovado' },
    { value: 'rejected', label: 'Rejeitado' },
    { value: 'pending', label: 'Pendente' },
    { value: 'processing', label: 'Processando' },
    { value: 'error', label: 'Erro' },
    { value: 'not_processed', label: 'Não processado' },
];

// Bureau status priority for sorting (worst first)
const bureauStatusPriority: Record<string, number> = { error: 0, rejected: 1, pending: 2, not_processed: 3, approved: 4 };

const BureauStatusFilter: React.FC<{ label: string; selected: string[]; onChange: (vals: string[]) => void }> = ({ label, selected, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const toggle = (val: string) => {
        const next = selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val];
        onChange(next);
    };

    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(!open)}
                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 text-left truncate flex items-center justify-between"
            >
                <span>{selected.length ? `${selected.length} selecionado(s)` : label}</span>
            </button>
            {open && (
                <div className="absolute z-50 mt-1 min-w-[160px] bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded shadow-lg">
                    {STATUS_OPTIONS.map(opt => (
                        <label key={opt.value} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer text-xs">
                            <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} className="w-3 h-3" />
                            {opt.label}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

const bureaus = [
    { code: 'BLOCKLIST', name: 'Blocklist' },
    { code: 'IBAMA', name: 'Ibama' },
    { code: 'SCR', name: 'SCR' },
    { code: 'CCIR', name: 'CCIR' },
    { code: 'ACRISCORE', name: 'AgroScore', pfOnly: true },
    { code: 'QUOD', name: 'Quod', resolve: (doc: string) => isCpf(doc) ? 'QUOD_PF' : 'QUOD' },
    { code: 'SERASA', name: 'Serasa', pjOnly: true },
    { code: 'BOAVISTA', name: 'BoaVista', pfOnly: true },
];

const getBureauStatusIcon = (status: string, isInactive?: boolean) => {
    if (isInactive) return <span className="text-gray-300 dark:text-gray-600">-</span>;

    const dotColors: Record<string, string> = {
        approved: 'bg-green-500',
        rejected: 'bg-red-500',
        pending: 'bg-yellow-500',
        processing: 'bg-blue-500',
        error: 'bg-violet-600',
    };

    const color = dotColors[status];
    if (!color) return <span className="text-gray-400">-</span>;
    return <span className={`inline-block w-3 h-3 rounded-full ${color}`} />;
};

export const BatchProcessingDetail: React.FC = () => {
    const { batchId } = useParams<{ batchId: string }>();
    const navigate = useNavigate();
    const [selectedDrawees, setSelectedDrawees] = useState<Set<string>>(new Set());
    const [batch, setBatch] = useState<BatchDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [draweeModalDoc, setDraweeModalDoc] = useState<string | null>(null);
    const [draweeModalItemId, setDraweeModalItemId] = useState<string | null>(null);
    const [isReprocessing, setIsReprocessing] = useState(false);

    // Filters
    const [filterName, setFilterName] = useState('');
    const [filterDoc, setFilterDoc] = useState('');
    const [filterType, setFilterType] = useState('');
    const [bureauStatusFilters, setBureauStatusFilters] = useState<Record<string, string[]>>({});
    const debouncedName = useDebounce(filterName);
    const debouncedDoc = useDebounce(filterDoc);

    // Sort: field + direction
    const [sortField, setSortField] = useState<string>('name');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

    const toggleSort = useCallback((field: string) => {
        setSortField(prev => {
            if (prev === field) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return field; }
            setSortDir('asc');
            return field;
        });
    }, []);

    useEffect(() => {
        if (!batchId) return;
        let stopped = false;
        const fetchDetail = () => {
            getBatchDetail(batchId)
                .then(data => {
                    setBatch(data);
                    if (data.status === 'COMPLETED' || data.status === 'ERROR') stopped = true;
                })
                .catch(() => toast.error('Erro ao carregar detalhes do lote'))
                .finally(() => setLoading(false));
        };
        fetchDetail();
        const interval = setInterval(() => {
            if (stopped) { clearInterval(interval); return; }
            fetchDetail();
        }, 30000);
        return () => clearInterval(interval);
    }, [batchId]);

    const drawees: DraweeDetail[] = useMemo(() => {
        if (!batch) return [];
        return batch.items.map((item: BatchDetailItem) => ({
            id: item.id,
            name: item.draweeName,
            document: item.document,
            pafDate: item.pafDate,
            errorMessage: item.errorMessage || null,
            bureauResults: bureaus.map(bureau => {
                const effectiveCode = bureau.resolve ? bureau.resolve(item.document) : bureau.code;
                const apiStatus = (item.bureauStatuses || {})[effectiveCode] || '';
                const apiError = (item.bureauErrors || {})[effectiveCode] || '';
                const mapped = apiStatus === 'SUCCESS' ? 'approved'
                    : apiStatus === 'REJECTED' ? 'rejected'
                    : apiStatus === 'ERROR' ? 'error'
                    : apiStatus === 'SKIPPED' ? 'not_processed'
                    : apiStatus === 'PROCESSING' ? 'processing'
                    : apiStatus === 'PENDING' ? 'pending'
                    : apiStatus ? 'pending'
                    : 'not_processed';
                return {
                    bureauName: bureau.name,
                    bureauCode: bureau.code,
                    status: mapped as 'approved' | 'rejected' | 'pending' | 'not_processed' | 'error',
                    reason: apiError || undefined,
                };
            }),
        }));
    }, [batch]);

    const filteredDrawees = useMemo(() => {
        let result = drawees;
        if (debouncedName) {
            const terms = debouncedName.split(';').map(t => t.trim().toLowerCase()).filter(Boolean);
            result = result.filter(d => terms.some(t => d.name.toLowerCase().includes(t)));
        }
        if (debouncedDoc) {
            const terms = debouncedDoc.split(';').map(t => unmaskDocument(t.trim())).filter(Boolean);
            result = result.filter(d => terms.some(t => unmaskDocument(d.document).includes(t)));
        }
        if (filterType) {
            result = result.filter(d => {
                return filterType === 'PF' ? isCpf(d.document) : !isCpf(d.document);
            });
        }
        // Bureau status filters
        Object.entries(bureauStatusFilters).forEach(([bureauCode, statuses]) => {
            if (statuses.length === 0) return;
            result = result.filter(d => {
                const br = d.bureauResults.find(b => b.bureauCode === bureauCode);
                return br && statuses.includes(br.status);
            });
        });

        // Sort
        result = [...result].sort((a, b) => {
            let cmp = 0;
            if (sortField === 'name') {
                cmp = a.name.localeCompare(b.name);
            } else if (sortField === 'document') {
                cmp = a.document.localeCompare(b.document);
            } else if (sortField === 'pafDate') {
                cmp = (a.pafDate || '').localeCompare(b.pafDate || '');
            } else {
                // Sort by specific bureau status
                const brA = a.bureauResults.find(br => br.bureauCode === sortField);
                const brB = b.bureauResults.find(br => br.bureauCode === sortField);
                cmp = (bureauStatusPriority[brA?.status || 'not_processed'] ?? 3) - (bureauStatusPriority[brB?.status || 'not_processed'] ?? 3);
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });

        return result;
    }, [drawees, debouncedName, debouncedDoc, filterType, sortField, sortDir, bureauStatusFilters]);

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedDrawees(new Set(filteredDrawees.map(d => d.id)));
        } else {
            setSelectedDrawees(new Set());
        }
    };

    const handleSelectDrawee = (id: string, checked: boolean) => {
        const newSelected = new Set(selectedDrawees);
        if (checked) {
            newSelected.add(id);
        } else {
            newSelected.delete(id);
        }
        setSelectedDrawees(newSelected);
    };

    const handleContinueAnalysis = async () => {
        if (!batchId || selectedDrawees.size === 0) return;
        try {
            const result = await continueAnalysis(batchId, Array.from(selectedDrawees));
            alert(`Análise continuada: ${result.itemCount} sacado(s). Pipeline: ${result.pipeline.join(', ')}`);
            setSelectedDrawees(new Set());
            // Reload batch detail
            const data = await getBatchDetail(batchId);
            setBatch(data);
        } catch (err: any) {
            alert(`Erro ao continuar análise: ${err.message || err}`);
        }
    };

    const handleRunSingleBureau = async (bureauCode: string) => {
        if (!batchId || selectedDrawees.size === 0) return;

        try {
            const result = await continueAnalysis(batchId, Array.from(selectedDrawees), bureauCode);
            const bureau = bureaus.find(b => b.code === bureauCode);
            alert(`Birô ${bureau?.name || bureauCode} disparado para ${result.itemCount} sacado(s).`);
            setSelectedDrawees(new Set());
            const data = await getBatchDetail(batchId);
            setBatch(data);
        } catch (err: any) {
            alert(`Erro: ${err.message || err}`);
        }
    };

    if (loading) {
        return <div className="p-4">Carregando...</div>;
    }

    if (!batch) {
        return <div className="p-4">Lote não encontrado</div>;
    }

    const renderRows = (data: DraweeDetail[]) => {
        return data.map((drawee) => {
            const isPJ = !isCpf(drawee.document);
            const isPF = isCpf(drawee.document);

            return (
                <tr key={drawee.id} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                    <td className="px-4 py-2">
                        <input
                            type="checkbox"
                            checked={selectedDrawees.has(drawee.id)}
                            onChange={(e) => handleSelectDrawee(drawee.id, e.target.checked)}
                            className="w-4 h-4"
                        />
                    </td>
                    <td className="px-4 py-2 text-sm">{drawee.name}</td>
                    <td className="px-4 py-2 text-sm font-mono whitespace-nowrap">
                        <button
                            onClick={() => { setDraweeModalDoc(drawee.document); setDraweeModalItemId(drawee.id); }}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                        >
                            {drawee.document}
                        </button>
                    </td>
                    <td className="px-4 py-2 text-sm text-center">
                        {isCpf(drawee.document) ? 'PF' : 'PJ'}
                    </td>
                    <td className="px-4 py-2 text-sm">
                        {drawee.pafDate || '-'}
                    </td>
                    {bureaus.map(bureau => {
                        const result = drawee.bureauResults.find(b => b.bureauCode === bureau.code);
                        const isInactive = (bureau.pjOnly && isPF) || (bureau.pfOnly && isPJ);

                        return (
                            <td key={bureau.code} className="px-4 py-2 text-center">
                                <div className="flex items-center justify-center gap-2">
                                    {result && getBureauStatusIcon(result.status, isInactive)}
                                    {result?.reason && !isInactive && (
                                        <span className="text-xs text-gray-500" title={result.reason}>ⓘ</span>
                                    )}
                                </div>
                            </td>
                        );
                    })}
                    <td className="px-4 py-2 text-sm">
                        {(() => {
                            const errors: string[] = [];
                            if (drawee.errorMessage) errors.push(drawee.errorMessage);
                            drawee.bureauResults.forEach(br => {
                                if (br.reason && br.status === 'error') errors.push(`${br.bureauCode}: ${br.reason}`);
                            });
                            if (!errors.length) return <span className="text-gray-400">-</span>;
                            return (
                                <div className="max-w-xs">
                                    {errors.map((e, i) => (
                                        <div key={i} className="text-xs text-red-600 dark:text-red-400 truncate" title={e}>{e}</div>
                                    ))}
                                </div>
                            );
                        })()}
                    </td>
                </tr>
            );
        });
    };

    const SortIcon: React.FC<{ field: string }> = ({ field }) => {
        if (sortField !== field) return <span className="text-gray-300 ml-1">⇅</span>;
        return sortDir === 'asc' ? <FiChevronUp className="inline ml-1" /> : <FiChevronDown className="inline ml-1" />;
    };

    return (
        <div className="h-full flex flex-col bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-slate-800 z-10 px-4 pt-6 pb-4">
                <Breadcrumb items={[
                    { label: 'Processamentos', path: '/credit-analysis/processamentos' },
                    { label: 'Detalhes do Lote' },
                ]} className="mb-2" />

                <div className="flex justify-between items-start">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-semibold">{batch.batchName}</h1>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                batch.status === 'COMPLETED' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : batch.status === 'ERROR' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            }`}>{batch.status === 'COMPLETED' ? 'Concluído' : batch.status === 'ERROR' ? 'Erro' : 'Processando'}</span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Número: {batch.batchNumber} | Criado em: {new Date(batch.createdAt).toLocaleString('pt-BR')}
                        </p>
                    </div>

                    <BatchActionBar
                        selectedCount={selectedDrawees.size}
                        isReprocessing={isReprocessing}
                        showReprocess={drawees.length > 0 && drawees.every(d => d.errorMessage)}
                        hasFilters={!!(filterName || filterDoc || filterType || Object.values(bureauStatusFilters).some(v => v.length > 0))}
                        bureaus={bureaus}
                        onReprocess={async () => {
                            if (!batchId) return;
                            setIsReprocessing(true);
                            try {
                                await reprocessBatch(batchId);
                                const data = await getBatchDetail(batchId);
                                setBatch(data);
                            } catch (err: any) {
                                alert(`Erro ao reprocessar: ${err.message || err}`);
                            } finally {
                                setIsReprocessing(false);
                            }
                        }}
                        onClearFilters={() => { setFilterName(''); setFilterDoc(''); setFilterType(''); setBureauStatusFilters({}); }}
                        onViewAnalysis={() => navigate(`/credit-analysis/batch/${batch.id}/analysis`)}
                        onRefreshPaf={async () => {
                            if (!batchId) return;
                            try {
                                const result = await refreshPaf(batchId, Array.from(selectedDrawees));
                                const data = await getBatchDetail(batchId);
                                setBatch(data);
                                setSelectedDrawees(new Set());
                                alert(`PAF atualizada para ${result.updated} sacado(s).`);
                            } catch (err) {
                                alert('Erro ao atualizar PAF.');
                                toast.error('Erro ao atualizar PAF');
                            }
                        }}
                        onContinueAnalysis={handleContinueAnalysis}
                        onRunSingleBureau={handleRunSingleBureau}
                    />
                </div>
            </div>

            {/* Tabela */}
            <div className="mx-4 mt-4 border border-gray-200 dark:border-slate-600 rounded">
                <div className="overflow-auto" style={{ height: 'calc(100vh - 190px)' }}>
                    <table className="w-full text-gray-900 dark:text-gray-100">
                        <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0 z-10">
                            <tr>
                                <th rowSpan={2} className="px-4 pt-1 pb-0 text-left align-middle bg-gray-50 dark:bg-slate-700">
                                    <input
                                        type="checkbox"
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                </th>
                                <th className="px-4 pt-1 pb-0 text-left text-sm font-semibold bg-gray-50 dark:bg-slate-700 cursor-pointer select-none" onClick={() => toggleSort('name')}>
                                    Sacado<SortIcon field="name" />
                                </th>
                                <th className="px-4 pt-1 pb-0 text-left text-sm font-semibold bg-gray-50 dark:bg-slate-700 cursor-pointer select-none" onClick={() => toggleSort('document')}>
                                    CPF/CNPJ<SortIcon field="document" />
                                </th>
                                <th className="px-4 pt-1 pb-0 text-center text-sm font-semibold bg-gray-50 dark:bg-slate-700" style={{ minWidth: '100px' }}>
                                    Tipo
                                </th>
                                <th className="px-4 pt-1 pb-0 text-left text-sm font-semibold bg-gray-50 dark:bg-slate-700 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('pafDate')}>
                                    Data da PAF<SortIcon field="pafDate" />
                                </th>
                                {bureaus.map(b => (
                                    <th key={b.code} className="px-4 pt-1 pb-0 text-center text-sm font-semibold bg-gray-50 dark:bg-slate-700 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort(b.code)}>
                                        {b.name}<SortIcon field={b.code} />
                                    </th>
                                ))}
                                <th className="px-4 pt-1 pb-0 text-left text-sm font-semibold bg-gray-50 dark:bg-slate-700">Erros</th>
                            </tr>
                            <tr className="bg-gray-50 dark:bg-slate-700">
                                <th className="px-4 pt-0 pb-2 bg-gray-50 dark:bg-slate-700">
                                    <input type="text" placeholder="Filtrar..." value={filterName} onChange={e => setFilterName(e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800" />
                                </th>
                                <th className="px-4 pt-0 pb-2 bg-gray-50 dark:bg-slate-700">
                                    <input type="text" placeholder="Filtrar..." value={filterDoc} onChange={e => setFilterDoc(e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800" />
                                </th>
                                <th className="px-4 pt-0 pb-2 bg-gray-50 dark:bg-slate-700">
                                    <select value={filterType} onChange={e => setFilterType(e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 appearance-none text-left">
                                        <option value="">Todos</option>
                                        <option value="PF">PF</option>
                                        <option value="PJ">PJ</option>
                                    </select>
                                </th>
                                <th className="px-4 pt-0 pb-2 bg-gray-50 dark:bg-slate-700" />
                                {bureaus.map(b => (
                                    <th key={b.code} className="px-2 pt-0 pb-2 bg-gray-50 dark:bg-slate-700">
                                        <BureauStatusFilter
                                            label="Filtrar"
                                            selected={bureauStatusFilters[b.code] || []}
                                            onChange={(vals) => setBureauStatusFilters(prev => ({ ...prev, [b.code]: vals }))}
                                        />
                                    </th>
                                ))}
                                <th className="px-4 pt-0 pb-2 bg-gray-50 dark:bg-slate-700" />
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-600">
                            {renderRows(filteredDrawees)}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Legenda + Progresso */}
            <div className="mx-4 mt-2 mb-4 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-green-500" /> Aprovado</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-red-500" /> Rejeitado</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-yellow-500" /> Pendente</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-blue-500" /> Processando</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-violet-600" /> Erro</span>
                </div>
                {(() => {
                    const processed = drawees.filter(d => d.bureauResults.some(b => b.status !== 'not_processed')).length;
                    const total = drawees.length;
                    const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
                    return (
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{pct}%</span>
                            <div className="w-24 h-2 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                                <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-sm text-gray-500">{processed} de {total} sacado(s) avaliado(s)</span>
                        </div>
                    );
                })()}
            </div>


            {draweeModalDoc && (
                <DraweeDetailsModal
                    isOpen={true}
                    onClose={() => { setDraweeModalDoc(null); setDraweeModalItemId(null); }}
                    document={draweeModalDoc}
                    itemId={draweeModalItemId}
                />
            )}
        </div>
    );
};
